import { useEffect, useState } from 'react';
import { withCDN } from 'helpers/cdn';
import { OVERLAY_TEXT_MAX_LENGTH } from 'helpers/overlayText';

/**
 * Overlay template registry — the CDN-published `overlay-templates/registry.json`
 * (assembled by fs-lambda's publish-overlay-templates.sh from each template's
 * `<id>.manifest.json`). ONE source for what templates exist and their per-template
 * facts;
 */
export type OverlayTemplateKind = 'lower-third' | 'scoreboard';

export type OverlayTemplateManifest = {
  id: string;
  kind: OverlayTemplateKind;
  timerColor?: string;
  frameColor?: string;
  followTextStyle?: 'full' | 'minimal';
  ribbonDivider?: 'arrow' | 'dotOrange' | 'dotGray';
};

export type OverlayDefaults = {
  templateId: string;
  title: string;
  subtitle: string;
  subtitleFallback: string;
  ribbonText: string;
};

export const OVERLAY_TEMPLATE_REGISTRY_PATH =
  '/overlay-templates/registry.json';

export const FALLBACK_OVERLAY_DEFAULTS: OverlayDefaults = {
  templateId: 'lower-third-01',
  title: 'LIVE on MILLIONS',
  subtitle: 'Watch {storeName} live on MILLIONS',
  subtitleFallback: 'Watch live on MILLIONS',
  ribbonText: 'FOLLOW MILLIONS',
};

let cache: readonly OverlayTemplateManifest[] = [];
let defaults: OverlayDefaults = FALLBACK_OVERLAY_DEFAULTS;
let pending: Promise<readonly OverlayTemplateManifest[]> | null = null;
let loaded = false;

const isManifest = (value: unknown): value is OverlayTemplateManifest =>
  !!value &&
  typeof (value as OverlayTemplateManifest).id === 'string' &&
  ((value as OverlayTemplateManifest).kind === 'lower-third' ||
    (value as OverlayTemplateManifest).kind === 'scoreboard');

const isDefaults = (value: unknown): value is Partial<OverlayDefaults> =>
  !!value &&
  typeof (value as OverlayDefaults).templateId === 'string' &&
  typeof (value as OverlayDefaults).title === 'string';

export const loadOverlayTemplateRegistry = (): Promise<
  readonly OverlayTemplateManifest[]
> => {
  if (pending) {
    return pending;
  }
  pending = (async () => {
    try {
      const response = await fetch(withCDN(OVERLAY_TEMPLATE_REGISTRY_PATH));
      if (!response.ok) {
        throw new Error(`registry ${response.status}`);
      }
      const body = (await response.json()) as {
        templates?: unknown[];
        defaults?: unknown;
      };
      const templates = (body.templates ?? []).filter(isManifest);
      if (!templates.length) {
        throw new Error('registry has no templates');
      }
      cache = templates;
      if (isDefaults(body.defaults)) {
        defaults = { ...FALLBACK_OVERLAY_DEFAULTS, ...body.defaults };
      }
    } catch (error) {
      console.warn('overlay template registry unavailable', error);
    }
    loaded = true;
    return cache;
  })();
  return pending;
};

export const getOverlayTemplateManifest = (
  templateId?: string | null
): OverlayTemplateManifest | undefined =>
  templateId ? cache.find((template) => template.id === templateId) : undefined;

export const getOverlayTemplatesByKind = (
  kind: OverlayTemplateKind
): OverlayTemplateManifest[] =>
  cache.filter((template) => template.kind === kind);

export const getOverlayDefaults = (): OverlayDefaults => defaults;

export const defaultOverlayTitle = (): string => defaults.title;

export const defaultOverlaySubtitle = (storeName?: string | null): string => {
  const withName = storeName
    ? defaults.subtitle.replace('{storeName}', storeName)
    : '';
  return withName && withName.length <= OVERLAY_TEXT_MAX_LENGTH
    ? withName
    : defaults.subtitleFallback;
};

export const useOverlayTemplateRegistry = (): {
  templates: readonly OverlayTemplateManifest[];
  loaded: boolean;
} => {
  const [state, setState] = useState({ templates: cache, loaded });

  useEffect(() => {
    let active = true;
    loadOverlayTemplateRegistry().then((templates) => {
      if (active) {
        setState({ templates, loaded: true });
      }
    });
    return () => {
      active = false;
    };
  }, []);

  return state;
};

export const resetOverlayTemplateRegistryForTests = (): void => {
  cache = [];
  defaults = FALLBACK_OVERLAY_DEFAULTS;
  pending = null;
  loaded = false;
};
